import React from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { provideIntlService, provideLocalizationService, registerForIntl, registerForLocalization } from '@progress/kendo-react-intl';
import { loadStudentCycles } from '../actions/student-cycles-actions';
import { Grid, GridSize, DateFormatter, InlineBadge, DatePeriodFormatter, DecimalFormatter } from '../../shared/components';
import { Colors, Text, WrapLayout } from '../../../ui';
import { CYCLE_STUDENT_STATUS } from '../../../resources/studyPlanCycleDepartmentStudentStatus';
import { withRouter } from '../../../components/withRouter';
import { getStudentCycleStatusColor } from '../../../utils/studyPlanCycleDepartmentStudentStatusColors';

const ColumnContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const HeaderWrapLayout = styled(WrapLayout)`
  line-height: 0
`;

const HeaderTitleContainer = styled(Text)`
  line-height: 1
`;

const CaptionText = styled(Text).attrs(props => ({
  textColor: Colors.GRAY_50,
  variant: 'caption'
}))``;

const CaptionWrapper = styled.div`
  ${props => props.width && `width: ${props.width};`}
  ${props => props.textAlign && `text-align: ${props.textAlign};`}
`;

const ClickableTr = styled.tr`
  cursor: pointer;
`;

const GroupHeaderContainer = styled.div`
  display: flex;
  align-items: center;
`;

const GroupHeaderStatsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  padding-left: 12px;
  font-weight: 600;
`;

class StudentCycles extends React.Component {
  constructor(props) {
    super(props);
    this.expandChange = this.expandChange.bind(this);
    this.getRowLink = this.getRowLink.bind(this);
    this.handleAddClicked = this.handleAddClicked.bind(this);
    this.handleEditClicked = this.handleEditClicked.bind(this);
    this.renderCycleHeader = this.renderCycleHeader.bind(this);
    this.renderStatusHeader = this.renderStatusHeader.bind(this);
    this.renderCell = this.renderCell.bind(this);
    this.renderStatus = this.renderStatus.bind(this);
    this.renderCycle = this.renderCycle.bind(this);
  }

  async componentDidMount() {
    await this.props.loadStudentCycles(this.props.studentId);
  }

  render() {
    const { studentCycles, loading } = this.props;
    const intlService = provideIntlService(this);
    const localizationService = provideLocalizationService(this);
    return (
      <Grid
        loading={loading}
        data={this.formatGridData(studentCycles, localizationService, intlService)}
        expandField='expanded'
        group={[{ field: 'year' }]}
        onExpandChange={this.expandChange}
        onRowClick={e => this.props.navigate(this.getRowLink(e.dataItem))}
        rowLink={this.getRowLink}
        cellRender={this.renderCell}
        rowRender={(tr, props) => {
          if (tr && props.rowType === 'groupHeader') {
            return <ClickableTr {...tr.props} className='' onClick={()=>{}}>{tr.props.children}</ClickableTr>;
          }
          return tr;
        }}
        isPageable={false}
        columns={[
          {
            field: 'cycle',
            className: 'align-left',
            headerClassName: 'align-left',
            title: localizationService.toLanguageString('residentCycles.cycle'),
            headerClassName: 'double-row-header',
            headerCell: this.renderCycleHeader,
            cell: this.renderCycle
          }, {
            field: 'localizedStatus',
            className: 'align-left',
            headerClassName: 'align-left',
            title: localizationService.toLanguageString('residentCycles.status'),
            minGridWidth: GridSize.large,
            headerClassName: 'double-row-header',
            headerCell: this.renderStatusHeader,
            cell: this.renderStatus
          }
        ]}
        actions={[
          { icon: 'edit', onClick: this.handleEditClicked }
        ]}
      />
    );
  }

  renderCycleHeader(e) {
    const { title } = e;
    const localizationService = provideLocalizationService(this);
    return (
      <>
        <HeaderTitleContainer variant='body2'>{title}</HeaderTitleContainer>
        <HeaderWrapLayout rowGap='0' columnGap='0'>
          <CaptionWrapper width={'20%'}><CaptionText>{localizationService.toLanguageString(`residentCycles.timePeriod`)}</CaptionText></CaptionWrapper>
          <CaptionWrapper width={'45%'} textAlign={'center'}><CaptionText>{localizationService.toLanguageString(`myStudies.credits`)}</CaptionText></CaptionWrapper>
          <CaptionWrapper width={'35%'}><CaptionText>{localizationService.toLanguageString(`residentCycles.cycleManagerAndEvaluation`)}</CaptionText></CaptionWrapper>
        </HeaderWrapLayout>
      </>
    );
  }
  renderCycle(e) {
    if (e.rowType === 'groupHeader') {
      return null;
    }
    const { className, dataItem } = e;
    const intlService = provideIntlService(this);
    const localizationService = provideLocalizationService(this);
    return (
      <td className={className}>
        <ColumnContainer>
          <div>
            {dataItem.cycle} {dataItem.accreditedCredits > 0 && <InlineBadge>{`${localizationService.toLanguageString(`residentCycles.accredited`)}: ${dataItem.accreditedCredits}`}</InlineBadge>}
          </div>
          <WrapLayout rowGap='0' columnGap='0'>
            <CaptionWrapper width={'20%'}>
              <CaptionText>
                {dataItem.dateFrom && dataItem.dateTo &&
                  DatePeriodFormatter(dataItem.dateFrom, dataItem.dateTo, intlService)
                }
              </CaptionText>
            </CaptionWrapper>
            <CaptionWrapper width={'45%'} textAlign={'center'}>
              <CaptionText>
                {dataItem.studentCredits &&
                  dataItem.studentCredits
                }
              </CaptionText>
            </CaptionWrapper>
            <CaptionWrapper width={'35%'}>
              <CaptionText>
                {dataItem.cycleManager &&
                  `${dataItem.grade ? `${dataItem.cycleManager} (${DecimalFormatter(dataItem.grade, 0)})` : dataItem.cycleManager}`
                }
              </CaptionText>
            </CaptionWrapper>
          </WrapLayout>
        </ColumnContainer>
      </td>
    );
  }

  renderStatusHeader(e) {
    const { title } = e;
    const localizationService = provideLocalizationService(this);
    return (
      <>
        <Text variant='body2'>{title}</Text>
        <CaptionWrapper width={'33%'}><CaptionText>{localizationService.toLanguageString(`residentCycles.changeDate`)}</CaptionText></CaptionWrapper>
      </>
    );
  }

  renderStatus(e) {
    if (e.rowType === 'groupHeader') {
      return null;
    }
    const { className, dataItem } = e;
    const localizationService = provideLocalizationService(this);
    const intlService = provideIntlService(this);
    return (
      <td className={className}>
        <ColumnContainer>
          {dataItem.status &&
            <InlineBadge themeColor={getStudentCycleStatusColor(dataItem.status)}>
              {localizationService.toLanguageString('studyPlanCycleDepartmentStudentStatus.' + dataItem.status)}
            </InlineBadge>
          }
          {dataItem.statusDate &&
            <CaptionText>
              {DateFormatter(dataItem.statusDate, intlService, true)}
            </CaptionText>
          }
        </ColumnContainer>
      </td>
    );
  }

  renderCell(td, props) {
    if (td && td.props.children && props.rowType === 'groupHeader') {
      const localizationService = provideLocalizationService(this);
      const studyYear = props.dataItem.items[0].year == 0
        ? `${localizationService.toLanguageString('residentCycles.notSpecify')}: `
        : `${props.dataItem.items[0].year} ${localizationService.toLanguageString('residentCycles.studyYear')}: `;
      const children = (
        <GroupHeaderContainer>
          <span onClick={() => this.expandChange({ dataItem: props.dataItem })}>{td.props.children.props.children[0]}</span>
          <GroupHeaderStatsContainer>
            {studyYear}
            {`${localizationService.toLanguageString('residentCycles.assignedCredits')} - ${props.dataItem.assignedCredits}, 
              ${localizationService.toLanguageString('residentCycles.finishedCredits')} - ${props.dataItem.finishedCredits}`
            }
            {props.dataItem.accreditedCredits > 0 &&
              `, ${localizationService.toLanguageString('residentCycles.accreditedCredits')} - ${props.dataItem.accreditedCredits}`
            }
          </GroupHeaderStatsContainer>
        </GroupHeaderContainer>
      );
      return React.cloneElement(td, { ...td.props, colSpan: 4, onClick: () => this.expandChange({ dataItem: props.dataItem }), style: { paddingLeft: '12px' } }, children);
    }
    return td;
  }

  expandChange(event) {
    event.dataItem.expanded = !event.dataItem.expanded;
    this.forceUpdate();
  };

  formatGridData = (studentCycles, localizationService, intlService) => {
    const studentCycleLastOccurrence = new Map();
    for (let i = 0; i < studentCycles?.data?.length; i++) {
      if (studentCycles.data[i].items) {
        for (let s = 0; s < studentCycles.data[i].items.length; s++) {
          studentCycleLastOccurrence.set(studentCycles.data[i].items[s].studyPlanCycleId, { group: i, item: s });
        }
      }
    }
    for (let i = 0; i < studentCycles?.data?.length; i++) {
      studentCycles.data[i].assignedCredits = 0;
      studentCycles.data[i].finishedCredits = 0;
      studentCycles.data[i].accreditedCredits = 0;
      if (studentCycles.data[i].items) {
        for (let s = 0; s < studentCycles.data[i].items.length; s++) {
          studentCycles.data[i].assignedCredits += studentCycles.data[i].items[s].studentCredits;
          studentCycles.data[i].finishedCredits += studentCycles.data[i].items[s].status === CYCLE_STUDENT_STATUS.FINISHED ? studentCycles.data[i].items[s].studentCredits : 0;
          const last = studentCycleLastOccurrence.get(studentCycles.data[i].items[s].studyPlanCycleId);
          if (last.group !== i || last.item !== s) {
            studentCycles.data[i].items[s].accreditedCredits = 0;
          }
          studentCycles.data[i].accreditedCredits += studentCycles.data[i].items[s].accreditedCredits;
          studentCycles.data[i].items[s].localizedCycleType = localizationService.toLanguageString(`cycleType.${studentCycles.data[i].items[s].cycleType}`);
          studentCycles.data[i].items[s].dateFromTo = `
          ${
            studentCycles.data[i].items[s].dateFrom
              ? DateFormatter(new Date(studentCycles.data[i].items[s].dateFrom), intlService)
              : '-'
          }
          ${
            studentCycles.data[i].items[s].dateFrom && studentCycles.data[i].items[s].dateTo
              ? '\n'
              : '/'
          }
          ${
            studentCycles.data[i].items[s].dateTo
              ? DateFormatter(new Date(studentCycles.data[i].items[s].dateTo), intlService)
              : '-'}
          `;
        }
      }
    }
    return studentCycles;
  };

  getRowLink(dataItem) {
    return dataItem.id ? `/students/${dataItem.studentId}/cycles/${dataItem.id}/overview` : null;
  }

  handleEditClicked(row) {
    this.props.navigate(`/study_plans/${row.dataItem.studyPlanId}/cycles/${row.dataItem.studyPlanCycleId}/students/${row.dataItem.id}`);
  }

  handleAddClicked() {
    this.props.navigate(`/students/${this.props.studentId}/cycles/new`);
  }
}

const mapStateToProps = (state) => ({
  studentCycles: state.studentCycles.studentCycles,
  loading: state.studentCycles.loading
});

const mapDispatchToProps = (dispatch) => ({
  loadStudentCycles: (payload) => dispatch(loadStudentCycles(payload))
});

registerForIntl(StudentCycles);
registerForLocalization(StudentCycles);

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(StudentCycles));
